import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Sprout, X, Key, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { TERMS_OF_SERVICE } from '../legal/terms';
import FarmLocationPicker from '../components/FarmLocationPicker';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [isAgronomist, setIsAgronomist] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', farm_name: '',
    farm_latitude: '', farm_longitude: '',
    accepted_terms: false, agronomist_key: '', agronomist_name: ''
  });
  const [farmLocation, setFarmLocation] = useState({ lat: '', lng: '' });
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => { if (inviteToken) verifyInvite(); }, [inviteToken]);

  const verifyInvite = async () => {
    try {
      const response = await axios.get(`${API}/invites/verify/${inviteToken}`);
      setInviteInfo(response.data);
      toast.success(`Invited by ${response.data.agronomist_name}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Invalid or expired invite'); }
  };

  const handleAgronomistKeyChange = (key) => {
    setFormData({ ...formData, agronomist_key: key });
    setIsAgronomist(key.length >= 3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accepted_terms) { toast.error('Please accept the Terms of Service'); setShowTerms(true); return; }

    if (isAgronomist && formData.agronomist_key) {
      if (!formData.agronomist_name || formData.agronomist_name.trim().length < 2) { toast.error('Please enter your name (min 2 characters)'); return; }
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/register-agronomist`, {
          email: formData.email, password: formData.password,
          agronomist_key: formData.agronomist_key, agronomist_name: formData.agronomist_name,
          accepted_terms: formData.accepted_terms
        });
        const { token, user } = response.data;
        localStorage.setItem('token', token); localStorage.setItem('user', JSON.stringify(user));
        toast.success('Agronomist registration successful!');
        navigate('/agronomist'); window.location.reload();
      } catch (error) { toast.error(error.response?.data?.detail || 'Registration failed'); }
      finally { setLoading(false); }
      return;
    }

    if (!farmLocation.lat || !farmLocation.lng) { toast.error('Please select your farm location on the map'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email, password: formData.password,
        farm_name: formData.farm_name,
        farm_latitude: parseFloat(farmLocation.lat), farm_longitude: parseFloat(farmLocation.lng),
        accepted_terms: formData.accepted_terms, invite_token: inviteToken || undefined
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token); localStorage.setItem('user', JSON.stringify(user));
      toast.success('Registration successful! 30-day free trial activated.');
      navigate('/'); window.location.reload();
    } catch (error) { toast.error(error.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="page-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card-floating anim-scale" style={{ position: 'relative', width: '100%', maxWidth: 420, padding: 28, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, var(--gold), var(--gold2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sprout style={{ width: 22, height: 22, color: '#0b241a' }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>SFarm Monitor</h1>
        </div>

        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, textAlign: 'center' }}>Create Account</h2>
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>{isAgronomist ? 'Register as Agronomist' : 'Get started with 30 days free trial'}</p>

        {inviteInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: 10, marginBottom: 14 }}>
            <UserPlus size={18} style={{ color: 'var(--success)', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>You've been invited!</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>by {inviteInfo.agronomist_name}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="register-form">
          <div><label className="label-depth">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input-depth" required /></div>

          <div><label className="label-depth">Password</label>
            <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="input-depth" required /></div>

          <div><label className="label-depth" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Key size={13} /> Agronomist Key (optional)</label>
            <input type="text" value={formData.agronomist_key} onChange={(e) => handleAgronomistKeyChange(e.target.value.toUpperCase())}
              placeholder="Enter key if you're an agronomist" className="input-depth" />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Leave empty to register as a farmer</p></div>

          {isAgronomist && (
            <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)', padding: 14, borderRadius: 12 }}>
              <label className="label-depth">Your Name (as Agronomist)</label>
              <input type="text" value={formData.agronomist_name} onChange={(e) => setFormData({ ...formData, agronomist_name: e.target.value })}
                placeholder="Dr. John Smith" className="input-depth" required={isAgronomist} />
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This name will be shown to farmers</p>
            </div>
          )}

          {!isAgronomist && (
            <>
              <div><label className="label-depth">Farm Name</label>
                <input type="text" value={formData.farm_name} onChange={(e) => setFormData({ ...formData, farm_name: e.target.value })} className="input-depth" required={!isAgronomist} /></div>
              <FarmLocationPicker value={farmLocation} onChange={setFarmLocation} />
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <input type="checkbox" id="terms" checked={formData.accepted_terms}
              onChange={(e) => setFormData({ ...formData, accepted_terms: e.target.checked })}
              style={{ marginTop: 4, accentColor: 'var(--gold)' }} required />
            <label htmlFor="terms" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              I agree to the{' '}
              <button type="button" onClick={() => setShowTerms(true)} style={{ color: 'var(--gold)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Terms of Service</button>
            </label>
          </div>

          <button type="submit" disabled={loading} data-testid="register-submit-button"
            className="btn-depth-primary" style={{ width: '100%', padding: '12px 18px', fontSize: 15 }}>
            {loading ? 'Creating account...' : (isAgronomist ? 'Register as Agronomist' : 'Register')}
          </button>
        </form>

        <p style={{ marginTop: 18, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--gold)', fontWeight: 600, textDecoration: 'none' }}>Login here</Link>
        </p>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="card-floating anim-scale" style={{ position: 'relative', maxWidth: 700, width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>Terms of Service</h2>
              <button onClick={() => setShowTerms(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={18} /></button>
            </div>
            <div style={{ overflowY: 'auto', padding: 18, flex: 1 }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Inter, sans-serif', fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{TERMS_OF_SERVICE}</pre>
            </div>
            <div style={{ padding: 16, borderTop: '1px solid var(--border-soft)', display: 'flex', gap: 12 }}>
              <button onClick={() => { setFormData({ ...formData, accepted_terms: true }); setShowTerms(false); }}
                className="btn-depth-primary" style={{ flex: 1 }}>I Accept</button>
              <button onClick={() => setShowTerms(false)} className="btn-depth-ghost" style={{ flex: 1 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
